import { createContext, useContext, type ReactNode } from 'react';
import type { MapPoint } from './map-style.ts';

/**
 * A way back for the unavailable panel.
 *
 * The engine choice and the loader probes are memoised once per session, so a
 * single transient failure — a token or CDN response slower than the ceiling, a
 * blip at page-open — would otherwise cache "unavailable" for the whole session
 * with no retry short of a full reload. `map.tsx` publishes a re-probe function
 * through this context; the panel offers it as a real, labelled "Try again"
 * button. Null (no provider) means retry is unavailable and the button hides,
 * so the panel still renders honestly in isolation (e.g. a unit test).
 */
const RetryContext = createContext<(() => void) | null>(null);

export function MapRetryProvider({
  onRetry,
  children,
}: {
  onRetry: () => void;
  children: ReactNode;
}) {
  return <RetryContext.Provider value={onRetry}>{children}</RetryContext.Provider>;
}

/**
 * The last resort, when neither map engine could be brought up: MapKit is not
 * configured (or its token could not be minted) and Google has no key, or both
 * scripts failed to load. It takes the SAME props as the two real engines so
 * the chooser in `map.tsx` can drop it in unchanged.
 *
 * The one rule of a map that cannot render on a live incident screen: it must
 * fail *honestly*. Never the old grey watermarked tile, never a blank broken
 * box — a plain panel that says the map is unavailable and keeps the layout
 * from collapsing under the controls beneath it. It reserves the full height
 * for exactly that reason — and, when a retry is available, offers a way back
 * so a transient blip is not a whole-session outage.
 */
export function Map({
  height = 320,
}: {
  points: MapPoint[];
  height?: number | string;
  dark?: boolean;
  onClick?: (latitude: number, longitude: number) => void;
  onSelect?: (pointId: string) => void;
  fallbackCenter?: { latitude: number; longitude: number };
}) {
  const onRetry = useContext(RetryContext);
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 rounded-[var(--radius-1)] border border-dashed border-[var(--line-2)] px-4 text-center"
      style={{ height }}
    >
      <p role="status" className="m-0 text-[13px] text-[var(--ink-2)]">
        The map can&rsquo;t be shown right now. Everything else on this screen is unaffected.
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="rounded-[var(--radius-1)] border border-[var(--line)] bg-[var(--paper)] px-3 py-1.5 text-[13px] font-medium text-[var(--ink)] outline-none hover:bg-[var(--paper-3)] focus:border-[var(--focus)]"
        >
          Try again
        </button>
      )}
    </div>
  );
}
